import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import { apiService } from "../api/services/apiService";
import { flattenPendingProducts } from "../utils/subscriptionProducts";

const SubscriptionRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as {
    productUuid?: string;
    returnPath?: string;
    returnSearch?: string;
  } | null;
  const productUuidFromState = locationState?.productUuid;

  const [requests, setRequests] = useState<Awaited<ReturnType<typeof apiService.getSubscriptionRequests>>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionKey, setActionKey] = useState<string | null>(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getSubscriptionRequests();
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load subscription requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const pendingRows = useMemo(() => flattenPendingProducts(requests), [requests]);

  const filteredRows = productUuidFromState
    ? pendingRows.filter((row) => row.product.uuid === productUuidFromState)
    : pendingRows;

  const handleApprove = async (subscriptionUuid: string, productUuid: string) => {
    const key = `${subscriptionUuid}:${productUuid}`;
    setActionKey(key);
    try {
      await apiService.approveSubscriptionProduct(subscriptionUuid, productUuid);
      await fetchRequests();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to approve");
    } finally {
      setActionKey(null);
    }
  };

  const handleRevoke = async (subscriptionUuid: string, productUuid: string) => {
    if (!window.confirm("Revoke this subscription request? The subscriber will lose access to this product.")) {
      return;
    }
    const key = `${subscriptionUuid}:${productUuid}`;
    setActionKey(key);
    try {
      await apiService.revokeSubscriptionProduct(subscriptionUuid, productUuid);
      await fetchRequests();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to revoke");
    } finally {
      setActionKey(null);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle="View Subscription Requests"
        pageDescription="Approve or revoke pending product enrollments within subscription requests for products you own."
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (locationState?.returnPath) {
                navigate(
                  `${locationState.returnPath}${locationState.returnSearch ?? ""}`,
                );
              } else {
                navigate("/manage-assets?tab=products");
              }
            }}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {locationState?.returnPath
              ? "Back to Products"
              : "Back to Manage Products"}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
              overflow: "hidden",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Subscription name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Subscriber</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No pending subscription requests.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map(({ subscription, product }) => {
                      const rowKey = `${subscription.uuid}:${product.uuid}`;
                      return (
                        <TableRow key={rowKey} hover>
                          <TableCell>{subscription.name}</TableCell>
                          <TableCell>{product.name ?? "—"}</TableCell>
                          <TableCell>{subscription.subscriberEmail ?? "—"}</TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleApprove(subscription.uuid, product.uuid)}
                              disabled={actionKey !== null}
                              sx={{ mr: 1, textTransform: "none" }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => handleRevoke(subscription.uuid, product.uuid)}
                              disabled={actionKey !== null}
                              sx={{ textTransform: "none" }}
                            >
                              Revoke
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default SubscriptionRequestsPage;
